import { useState, useRef, useCallback } from "react";

// ============================================================================
//  DiseñadorCuestionario — Editor visual para armar el formulario OMR.
//  Arrastrás cada campo a su posición en la hoja A4, con snap a grilla. Cada
//  campo se dibuja como se vera impreso (burbujas reales). Multipagina: cuando
//  un campo se suelta mas abajo del alto de hoja, cae en la pagina siguiente.
//
//  Esta es la Capa 1: el lienzo y el arrastre, con campos de ejemplo.
//  (Capa 2: conectar parser. Capa 3: exportar spec al generador.)
//
//  Coordenadas internas en PUNTOS (pt), como el generador del back:
//  A4 = 595.28 x 841.89 pt. La pantalla escala esos puntos a pixeles.
// ============================================================================

const PAGE_W = 595.28;
const PAGE_H = 841.89;
const MARGEN = 40;
const RADIO = 7;
const GRID = 10;            // snap cada 10pt
const ESCALA = 0.85;        // pt -> px en pantalla

// Paleta DESEM Junior Achievement (extraída del logo)
const TEAL = "#286076";           // teal oscuro: color principal
const TEAL_BRILLANTE = "#19A0AF"; // cyan de los rombos: acento
const TEAL_OSC = "#1C4655";       // teal más profundo para hover/detalles
const TINTA = "#1a2b31";          // texto principal con tinte teal

// snap de un valor a la grilla
const snap = (v) => Math.round(v / GRID) * GRID;

// ---- campos de ejemplo para ver el arrastre ya ----
const EJEMPLO = [
  {
    uid: "c1", id: "tfa_1", tipo: "grilla", pregunta: "Cédula de identidad",
    digitos: 8, x: MARGEN, y: 70,
  },
  {
    uid: "c2", id: "tfa_4953", tipo: "opcion", pregunta: "Género",
    orientacion: "horizontal", x: MARGEN, y: 300,
    opciones: [
      { valorId: "tfa_4954", etiqueta: "Femenino" },
      { valorId: "tfa_4955", etiqueta: "Masculino" },
      { valorId: "tfa_4956", etiqueta: "No binario" },
    ],
  },
  {
    uid: "c3", id: "tfa_1961", tipo: "likert",
    pregunta: "Antes de comprar algo, considero si puedo pagarlo",
    x: MARGEN, y: 400,
    opciones: [
      { valorId: "tfa_4517", etiqueta: "Muy en desacuerdo" },
      { valorId: "tfa_4518", etiqueta: "En desacuerdo" },
      { valorId: "tfa_4519", etiqueta: "Ni de acuerdo ni en desacuerdo" },
      { valorId: "tfa_4631", etiqueta: "De acuerdo" },
      { valorId: "tfa_4632", etiqueta: "Muy de acuerdo" },
    ],
  },
  {
    uid: "c4", id: "tfa_5017", tipo: "opcion", pregunta: "¿Qué es un presupuesto?",
    orientacion: "vertical", x: MARGEN, y: 480,
    opciones: [
      { valorId: "tfa_5018", etiqueta: "Una suposición básica" },
      { valorId: "tfa_5019", etiqueta: "Una herramienta para administrar el dinero" },
      { valorId: "tfa_5020", etiqueta: "Algo creado por un experto" },
      { valorId: "tfa_5022", etiqueta: "No lo sé" },
    ],
  },
];

// alto estimado de un campo en pt (para saber si entra en la pagina)
function altoCampo(c) {
  if (c.tipo === "grilla") return 16 + 10 * 15 + 8;
  if (c.tipo === "likert") return 14 + 22;
  if (c.tipo === "texto") return 16 + 20;
  if (c.tipo === "opcion") {
    return c.orientacion === "horizontal" ? 16 + 20 : 16 + c.opciones.length * 15 + 4;
  }
  return 40;
}
function anchoCampo(c) {
  if (c.tipo === "grilla") return Math.min(26, (PAGE_W - 2 * MARGEN - 20) / c.digitos) * c.digitos + 26;
  if (c.tipo === "likert") return PAGE_W - 2 * MARGEN;
  if (c.tipo === "texto") return c.ancho || 260;
  if (c.tipo === "opcion" && c.orientacion === "horizontal") return PAGE_W - 2 * MARGEN;
  // vertical: ancho del texto mas largo, aprox
  const maxLbl = Math.max(...c.opciones.map((o) => o.etiqueta.length));
  return Math.min(PAGE_W - 2 * MARGEN, 40 + maxLbl * 5);
}

export default function DiseñadorCuestionario({ baseApi = "https://pideapi2025.azurewebsites.net" }) {
  const [campos, setCampos] = useState(EJEMPLO);
  const [selId, setSelId] = useState(null);
  const [paginas, setPaginas] = useState(1);
  const arrastre = useRef(null);

  // --- panel de preguntas del parser ---
  const [html, setHtml] = useState("");
  const [analizando, setAnalizando] = useState(false);
  const [errorParser, setErrorParser] = useState("");
  const [disponibles, setDisponibles] = useState([]); // detectadas sin usar
  const dragData = useRef(null);   // pregunta que se arrastra desde el panel
  const lienzoRef = useRef(null);

  // llamar al parser del back con el HTML pegado
  const analizarHtml = async () => {
    if (!html.trim()) return;
    setAnalizando(true);
    setErrorParser("");
    try {
      const fd = new FormData();
      fd.append("html", html);
      const res = await fetch(`${baseApi}/api/FormParser/analizar`, {
        method: "POST", body: fd,
      });

      // leer como texto primero, para poder diagnosticar si NO es JSON
      const texto = await res.text();
      let data;
      try {
        data = JSON.parse(texto);
      } catch {
        // el back devolvio HTML (pagina de error 404/500), no JSON
        throw new Error(
          !res.ok
            ? `El servidor respondió ${res.status} con una página, no JSON. ` +
              `Revisá que la URL '${baseApi}/api/FormParser/analizar' sea correcta y que el back esté corriendo.`
            : "La respuesta no es JSON válido. Revisá el endpoint del parser."
        );
      }

      if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
      const usados = new Set(campos.map((c) => c.id));
      setDisponibles((data.preguntas || []).filter((p) => !usados.has(p.id)));
    } catch (err) {
      setErrorParser(err.message || "No se pudo analizar el HTML.");
    } finally {
      setAnalizando(false);
    }
  };

  // convertir una pregunta del parser en un campo del editor
  const preguntaACampo = (p, x, y) => {
    // tipoSugerido del parser: "texto" | "opcion" | "likert" | "escala"
    let tipo = p.tipoSugerido;
    // los "texto": decidir si van como grilla de digitos (CI, tel) o texto libre
    if (tipo === "texto") {
      tipo = pareceNumerico(p.pregunta) ? "grilla" : "texto";
    }
    // "escala" (NPS 1-10) lo tratamos como likert (fila de burbujas)
    if (tipo === "escala") tipo = "likert";

    const base = {
      uid: "u" + Date.now() + Math.random().toString(36).slice(2, 6),
      id: p.id, tipo, pregunta: p.pregunta, x: snap(x), y: snap(y),
    };
    if (tipo === "grilla") base.digitos = pareceCelular(p.pregunta) ? 9 : 8;
    if (tipo === "texto") base.ancho = 260;  // largo de la linea en pt
    if (tipo === "opcion") { base.orientacion = "vertical"; base.opciones = p.opciones; }
    if (tipo === "likert") base.opciones = p.opciones;
    return base;
  };

  // heuristica: la pregunta pide un numero de identificacion/telefono?
  const pareceNumerico = (txt) => /c[ée]dula|documento|identidad|celular|tel[ée]fono|n[úu]mero/i.test(txt);
  const pareceCelular = (txt) => /celular|tel[ée]fono/i.test(txt);

  // en que pagina cae un campo segun su Y (cada pagina es un tramo de PAGE_H util)
  const paginaDe = (y) => Math.floor(y / (PAGE_H - MARGEN)) ;

  // recalcular cuantas paginas hacen falta
  const recalcularPaginas = useCallback((lista) => {
    let maxPag = 0;
    lista.forEach((c) => {
      const pag = Math.floor((c.y + altoCampo(c)) / (PAGE_H - MARGEN));
      if (pag > maxPag) maxPag = pag;
    });
    setPaginas(maxPag + 1);
  }, []);

  // ---- arrastre ----
  const onDown = (e, c) => {
    e.stopPropagation();
    setSelId(c.uid);
    const startX = e.clientX, startY = e.clientY;
    const origX = c.x, origY = c.y;
    arrastre.current = { uid: c.uid };

    const move = (ev) => {
      const dx = (ev.clientX - startX) / ESCALA;
      const dy = (ev.clientY - startY) / ESCALA;
      setCampos((prev) => prev.map((f) =>
        f.uid === c.uid
          ? { ...f, x: Math.max(MARGEN, snap(origX + dx)), y: Math.max(20, snap(origY + dy)) }
          : f
      ));
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      arrastre.current = null;
      setCampos((prev) => { recalcularPaginas(prev); return prev; });
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  // ---- soltar una pregunta del panel sobre el lienzo ----
  const onDropLienzo = (e) => {
    e.preventDefault();
    const p = dragData.current;
    if (!p) return;
    // posicion del drop relativa al lienzo, convertida a pt
    const cont = lienzoRef.current.getBoundingClientRect();
    const scrollTop = lienzoRef.current.scrollTop;
    // x/y en px dentro del area de hojas; aproximamos a pt dividiendo por ESCALA
    // (el usuario luego ajusta fino con el arrastre normal)
    const xPx = e.clientX - cont.left;
    const yPx = e.clientY - cont.top + scrollTop;
    const xPt = Math.max(MARGEN, xPx / ESCALA);
    const yPt = Math.max(20, yPx / ESCALA);

    const nuevo = preguntaACampo(p, xPt, yPt);
    setCampos((prev) => {
      const next = [...prev, nuevo];
      recalcularPaginas(next);
      return next;
    });
    setDisponibles((prev) => prev.filter((q) => q.id !== p.id));
    setSelId(nuevo.uid);
    dragData.current = null;
  };

  const sel = campos.find((c) => c.uid === selId);

  // ---- generar PDF + plantilla y descargar ----
  const [generando, setGenerando] = useState(false);
  const [errorGen, setErrorGen] = useState("");
  const [nombrePlantilla, setNombrePlantilla] = useState("");
  const [numeroFormulario, setNumeroFormulario] = useState("");
  const [plantillaGuardada, setPlantillaGuardada] = useState(null);

  const generarPdf = async () => {
    setGenerando(true);
    setErrorGen("");
    try {
      // armar la spec con las POSICIONES del editor.
      // x del campo es relativo a la hoja; y lo convertimos a y-dentro-de-su-pagina.
      const spec = {
        titulo: "Cuestionario DESEM",
        nombre: nombrePlantilla || "Cuestionario DESEM",
        numeroFormulario: numeroFormulario,
        paginas,
        campos: campos.map((c) => {
          const pag = Math.floor(c.y / (PAGE_H - MARGEN));
          const yLocal = c.y - pag * (PAGE_H - MARGEN);
          return {
            id: c.id, tipo: c.tipo, pregunta: c.pregunta,
            pagina: pag, x: c.x, y: yLocal,
            digitos: c.digitos, ancho: c.ancho,
            orientacion: c.orientacion,
            opciones: c.opciones || [],
          };
        }),
      };

      const res = await fetch(`${baseApi}/api/Generador/generar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(spec),
      });
      const texto = await res.text();
      let data;
      try { data = JSON.parse(texto); }
      catch {
        throw new Error(`El servidor respondió ${res.status} sin JSON. Revisá la URL '${baseApi}/api/Generador/generar'.`);
      }
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`);

      // guardar el id de la plantilla que quedó en la base
      if (data.plantillaId) setPlantillaGuardada(data.plantillaId);

      // 1) descargar el PDF (de base64)
      descargarBase64(data.pdfBase64, "application/pdf", "cuestionario.pdf");
      // 2) descargar la plantilla JSON (respaldo local)
      const blobTpl = new Blob([JSON.stringify(data.plantilla, null, 2)], { type: "application/json" });
      descargarBlob(blobTpl, "plantilla.json");
    } catch (err) {
      setErrorGen(err.message || "No se pudo generar.");
    } finally {
      setGenerando(false);
    }
  };

  const actualizar = (uid, cambios) =>
    setCampos((prev) => {
      const next = prev.map((c) => (c.uid === uid ? { ...c, ...cambios } : c));
      recalcularPaginas(next);
      return next;
    });

  // cambiar el tipo de un campo, seteando los defaults que le faltan
  const cambiarTipo = (uid, nuevoTipo) =>
    setCampos((prev) => {
      const next = prev.map((c) => {
        if (c.uid !== uid) return c;
        const base = { ...c, tipo: nuevoTipo };
        if (nuevoTipo === "grilla" && !base.digitos) base.digitos = 8;
        if (nuevoTipo === "texto" && !base.ancho) base.ancho = 260;
        if ((nuevoTipo === "opcion" || nuevoTipo === "likert") && !base.opciones) base.opciones = [];
        if (nuevoTipo === "opcion" && !base.orientacion) base.orientacion = "vertical";
        return base;
      });
      recalcularPaginas(next);
      return next;
    });

  return (
    <div style={S.app}>
      {/* ---------- PANEL IZQUIERDO: preguntas del parser ---------- */}
      <aside style={S.panelIzq}>
        <div style={S.panelHead}>
          <h2 style={S.h2}>Preguntas del formulario</h2>
          <p style={S.sub}>Pegá el HTML del form y arrastrá las preguntas a la hoja.</p>
        </div>
        <div style={S.izqBody}>
          <textarea
            style={S.htmlBox}
            placeholder="Pegá acá el HTML del formulario de FormAssembly…"
            value={html}
            onChange={(e) => setHtml(e.target.value)}
          />
          <button
            style={{ ...S.btnAnalizar, opacity: analizando || !html.trim() ? 0.5 : 1 }}
            disabled={analizando || !html.trim()}
            onClick={analizarHtml}
          >
            {analizando ? "Analizando…" : "Analizar HTML"}
          </button>
          {errorParser && <div style={S.errorParser}>{errorParser}</div>}

          <div style={S.listaPreg}>
            {disponibles.length === 0 ? (
              <p style={S.izqVacio}>
                {html.trim()
                  ? "Analizá el HTML para ver las preguntas."
                  : "Todavía no cargaste ningún formulario."}
              </p>
            ) : (
              disponibles.map((p) => (
                <div
                  key={p.id}
                  draggable
                  onDragStart={() => { dragData.current = p; }}
                  style={S.pregItem}
                >
                  <div style={S.pregTop}>
                    <span style={{ ...S.pregTipo, ...tipoBadge(p.tipoSugerido) }}>
                      {tipoCorto(p.tipoSugerido)}
                    </span>
                    <span style={S.pregId}>{p.id}</span>
                  </div>
                  <div style={S.pregTexto}>{p.pregunta}</div>
                  {p.opciones?.length > 0 && (
                    <div style={S.pregOpc}>{p.opciones.length} opciones</div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </aside>

      {/* ---------- LIENZO ---------- */}
      <div
        ref={lienzoRef}
        style={S.lienzo}
        onMouseDown={() => setSelId(null)}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDropLienzo}
      >
        <div style={S.lienzoInner}>
          {Array.from({ length: paginas }).map((_, pIdx) => (
            <Hoja key={pIdx} numero={pIdx + 1}>
              {campos
                .filter((c) => Math.floor(c.y / (PAGE_H - MARGEN)) === pIdx)
                .map((c) => (
                  <CampoRender
                    key={c.uid}
                    campo={c}
                    yLocal={c.y - pIdx * (PAGE_H - MARGEN)}
                    seleccionado={selId === c.uid}
                    onDown={onDown}
                  />
                ))}
            </Hoja>
          ))}
        </div>
      </div>

      {/* ---------- PANEL ---------- */}
      <aside style={S.panel}>
        <div style={S.panelHead}>
          <div style={S.marca}>DESEM Junior Achievement</div>
          <h1 style={S.h1}>Diseñar formulario</h1>
          <p style={S.sub}>Arrastrá cada campo a su lugar. Se alinea solo a la grilla.</p>
        </div>

        <div style={S.panelBody}>
          <div style={S.stat}>
            <span style={S.statNum}>{campos.length}</span>
            <span style={S.statLbl}>campos</span>
            <span style={{ ...S.statNum, marginLeft: 18 }}>{paginas}</span>
            <span style={S.statLbl}>{paginas === 1 ? "hoja" : "hojas"}</span>
          </div>

          {sel ? (
            <div style={S.propGroup}>
              <div style={S.propTit}>Campo seleccionado</div>
              <label style={S.lbl}>Pregunta</label>
              <input style={S.input} value={sel.pregunta}
                onChange={(e) => actualizar(sel.uid, { pregunta: e.target.value })} />

              <label style={S.lbl}>Identificador</label>
              <input style={{ ...S.input, fontFamily: "monospace", color: "#6b706e" }}
                value={sel.id} readOnly />

              <div style={S.propRow}>
                <div style={{ flex: 1 }}>
                  <label style={S.lbl}>Posición X</label>
                  <input style={S.input} type="number" value={sel.x}
                    onChange={(e) => actualizar(sel.uid, { x: snap(+e.target.value) })} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={S.lbl}>Posición Y</label>
                  <input style={S.input} type="number" value={sel.y}
                    onChange={(e) => actualizar(sel.uid, { y: snap(+e.target.value) })} />
                </div>
              </div>

              <label style={S.lbl}>Tipo de campo</label>
              <div style={S.segmento}>
                {["texto", "grilla", "opcion", "likert"].map((t) => (
                  <button key={t}
                    style={{ ...S.segBtn, ...(sel.tipo === t ? S.segBtnOn : {}), fontSize: 11 }}
                    onClick={() => cambiarTipo(sel.uid, t)}>
                    {tipoCortoEditor(t)}
                  </button>
                ))}
              </div>

              {sel.tipo === "opcion" && (
                <>
                  <label style={S.lbl}>Orientación</label>
                  <div style={S.segmento}>
                    {["vertical", "horizontal"].map((o) => (
                      <button key={o}
                        style={{ ...S.segBtn, ...(sel.orientacion === o ? S.segBtnOn : {}) }}
                        onClick={() => actualizar(sel.uid, { orientacion: o })}>
                        {o === "vertical" ? "Vertical" : "Horizontal"}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {sel.tipo === "grilla" && (
                <>
                  <label style={S.lbl}>Dígitos</label>
                  <input style={S.input} type="number" min={1} max={12} value={sel.digitos}
                    onChange={(e) => actualizar(sel.uid, { digitos: +e.target.value })} />
                </>
              )}

              {sel.tipo === "texto" && (
                <>
                  <label style={S.lbl}>Largo de la línea (pt)</label>
                  <input style={S.input} type="number" min={60} max={500} step={10}
                    value={sel.ancho || 260}
                    onChange={(e) => actualizar(sel.uid, { ancho: +e.target.value })} />
                </>
              )}

              <div style={S.tipoTag}>{tipoLabel(sel.tipo)}</div>
            </div>
          ) : (
            <div style={S.vacio}>
              <p style={{ margin: 0, color: "#8a8f8d" }}>
                Hacé clic en un campo para editarlo, o arrastralo para moverlo.
              </p>
            </div>
          )}
        </div>

        <div style={S.panelFoot}>
          {errorGen && <div style={S.errorParser}>{errorGen}</div>}
          {plantillaGuardada && (
            <div style={S.avisoGuardado}>
              Plantilla guardada (id {plantillaGuardada}). Ya se puede usar para escanear.
            </div>
          )}
          <input
            style={{ ...S.input, marginBottom: 8 }}
            placeholder="Nombre de la plantilla (ej. Domino 2026)"
            value={nombrePlantilla}
            onChange={(e) => setNombrePlantilla(e.target.value)}
          />
          <input
            style={{ ...S.input, marginBottom: 8 }}
            placeholder="N° de formulario (se imprime en la hoja)"
            value={numeroFormulario}
            onChange={(e) => setNumeroFormulario(e.target.value)}
          />
          <button
            style={{ ...S.btnGenerar, opacity: generando || campos.length === 0 ? 0.5 : 1 }}
            disabled={generando || campos.length === 0}
            onClick={generarPdf}
          >
            {generando ? "Generando…" : "Generar PDF y guardar plantilla"}
          </button>
          <p style={S.footHint}>Genera el PDF, lo descarga, y guarda la plantilla en la base.</p>
        </div>
      </aside>
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Hoja A4
// ---------------------------------------------------------------------------
function Hoja({ numero, children }) {
  const w = PAGE_W * ESCALA;
  const h = PAGE_H * ESCALA;
  const off = 22 * ESCALA;
  const fid = 14 * ESCALA;
  return (
    <div style={{ ...HS.hoja, width: w, height: h }}>
      {/* grilla de fondo */}
      <div style={{ ...HS.grid, backgroundSize: `${GRID * ESCALA}px ${GRID * ESCALA}px` }} />
      {/* fiduciales */}
      {[[off, off], [w - off - fid, off], [off, h - off - fid], [w - off - fid, h - off - fid]]
        .map(([x, y], i) => (
          <div key={i} style={{ ...HS.fid, left: x, top: y, width: fid, height: fid }} />
        ))}
      <div style={HS.numero}>Hoja {numero}</div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Render de un campo (fiel a como se imprime)
// ---------------------------------------------------------------------------
function CampoRender({ campo, yLocal, seleccionado, onDown }) {
  const x = campo.x * ESCALA;
  const y = yLocal * ESCALA;

  return (
    <div
      onMouseDown={(e) => onDown(e, campo)}
      style={{
        ...CS.wrap,
        left: x, top: y,
        outline: seleccionado ? `2px solid ${TEAL}` : "1px dashed transparent",
        boxShadow: seleccionado ? `0 0 0 4px ${TEAL}22` : "none",
      }}
    >
      <div style={CS.pregunta}>{campo.pregunta}</div>
      {campo.tipo === "grilla" && <Grilla campo={campo} />}
      {campo.tipo === "likert" && <Likert campo={campo} />}
      {campo.tipo === "opcion" && <Opcion campo={campo} />}
      {campo.tipo === "texto" && <Texto campo={campo} />}
    </div>
  );
}

function Burbuja({ size = RADIO * 2 * ESCALA }) {
  return <div style={{ ...CS.burbuja, width: size, height: size }} />;
}

function Grilla({ campo }) {
  const pasoX = Math.min(26, (PAGE_W - 2 * MARGEN - 20) / campo.digitos) * ESCALA;
  const pasoY = 15 * ESCALA;
  return (
    <div style={{ position: "relative", marginTop: 4 }}>
      {/* etiquetas 0-9 */}
      <div style={{ position: "absolute", left: -2, top: 0 }}>
        {Array.from({ length: 10 }).map((_, f) => (
          <div key={f} style={{ ...CS.digitoLbl, top: f * pasoY }}>{f}</div>
        ))}
      </div>
      <div style={{ marginLeft: 14 }}>
        {Array.from({ length: 10 }).map((_, fila) => (
          <div key={fila} style={{ display: "flex", height: pasoY, alignItems: "center" }}>
            {Array.from({ length: campo.digitos }).map((_, col) => (
              <div key={col} style={{ width: pasoX, display: "flex", justifyContent: "center" }}>
                <Burbuja />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function Likert({ campo }) {
  const ancho = (PAGE_W - 2 * MARGEN - 40) * ESCALA;
  const paso = ancho / campo.opciones.length;
  return (
    <div style={{ position: "relative", height: 30 * ESCALA, width: ancho + 40 * ESCALA, marginTop: 4 }}>
      {campo.opciones.map((o, i) => (
        <div key={i} style={{
          position: "absolute", left: 20 * ESCALA + i * paso + paso / 2 - RADIO * ESCALA,
          top: 4, textAlign: "center",
        }}>
          <Burbuja />
          <div style={CS.numLikert}>{i + 1}</div>
        </div>
      ))}
    </div>
  );
}

function Opcion({ campo }) {
  if (campo.orientacion === "horizontal") {
    const ancho = (PAGE_W - 2 * MARGEN - 40) * ESCALA;
    const paso = ancho / campo.opciones.length;
    return (
      <div style={{ position: "relative", height: 24 * ESCALA, width: ancho + 40 * ESCALA, marginTop: 4 }}>
        {campo.opciones.map((o, i) => (
          <div key={i} style={{
            position: "absolute", left: 20 * ESCALA + i * paso + paso / 2 - RADIO * ESCALA, top: 2,
            display: "flex", alignItems: "center", gap: 3, whiteSpace: "nowrap",
          }}>
            <Burbuja />
            <span style={CS.optLbl}>{o.etiqueta}</span>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div style={{ marginTop: 4 }}>
      {campo.opciones.map((o, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 5, height: 15 * ESCALA }}>
          <Burbuja />
          <span style={CS.optLbl}>{o.etiqueta}</span>
        </div>
      ))}
    </div>
  );
}

const tipoLabel = (t) => ({ grilla: "Grilla de dígitos", opcion: "Opción única", likert: "Escala Likert", texto: "Campo de texto" }[t] || t);
const tipoCortoEditor = (t) => ({ texto: "Texto", grilla: "Dígitos", opcion: "Opción", likert: "Likert" }[t] || t);

// descargar un base64 como archivo
function descargarBase64(base64, mime, nombre) {
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  descargarBlob(new Blob([arr], { type: mime }), nombre);
}
function descargarBlob(blob, nombre) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = nombre;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// campo de texto: una linea horizontal para escribir a mano encima
function Texto({ campo }) {
  const ancho = (campo.ancho || 260) * ESCALA;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ width: ancho, borderBottom: `1.4px solid ${TINTA}`, height: 18 * ESCALA }} />
    </div>
  );
}


// badge para el tipoSugerido del parser (texto/opcion/likert)
const tipoCorto = (t) => ({ texto: "Grilla", opcion: "Opción", likert: "Likert" }[t] || t);
const tipoBadge = (t) => ({
  texto:  { background: "#E6EEF1", color: TEAL_OSC },
  opcion: { background: "#E1F0F2", color: "#127a86" },
  likert: { background: "#DDEAEE", color: TEAL },
}[t] || { background: "#eee", color: "#666" });

// ---------------------------------------------------------------------------
//  estilos
// ---------------------------------------------------------------------------
const S = {
  app: {
    display: "grid", gridTemplateColumns: "290px 1fr 340px", height: "100vh",
    fontFamily: "-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif", color: TINTA,
    background: "#E7E9E6",
  },
  // panel izquierdo (preguntas del parser)
  panelIzq: {
    borderRight: "1px solid #d3d8d4", background: "#fff", display: "flex",
    flexDirection: "column", height: "100vh", overflow: "hidden",
  },
  h2: { fontSize: 15, fontWeight: 700, margin: 0, letterSpacing: "-.01em" },
  izqBody: { flex: 1, overflowY: "auto", padding: "14px 16px", display: "flex", flexDirection: "column" },
  htmlBox: {
    width: "100%", height: 90, border: "1px solid #d3d8d4", borderRadius: 8, padding: 9,
    fontSize: 11, fontFamily: "monospace", resize: "vertical", boxSizing: "border-box",
    outlineColor: TEAL, marginBottom: 8,
  },
  btnAnalizar: {
    background: TEAL, color: "#fff", border: "none", borderRadius: 8, padding: "9px",
    fontSize: 13, fontWeight: 600, cursor: "pointer",
  },
  errorParser: {
    marginTop: 8, padding: "8px 10px", background: "#FBEEEA", border: "1px solid #F0D2C8",
    borderRadius: 7, fontSize: 12, color: "#9a3a1a",
  },
  listaPreg: { marginTop: 12, display: "flex", flexDirection: "column", gap: 7 },
  izqVacio: { fontSize: 12, color: "#a0a5a2", lineHeight: 1.5, margin: "8px 0" },
  pregItem: {
    border: "1px solid #e2e6e3", borderRadius: 8, padding: "9px 10px", background: "#fff",
    cursor: "grab", transition: "border-color .12s",
  },
  pregTop: { display: "flex", alignItems: "center", gap: 6, marginBottom: 4 },
  pregTipo: { fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4, textTransform: "uppercase" },
  pregId: { fontSize: 10, color: "#a0a5a2", fontFamily: "monospace", marginLeft: "auto" },
  pregTexto: { fontSize: 12, color: TINTA, lineHeight: 1.35 },
  pregOpc: { fontSize: 10, color: "#8a8f8d", marginTop: 3 },

  lienzo: { overflow: "auto", padding: 40, display: "flex", justifyContent: "center" },
  lienzoInner: { display: "flex", flexDirection: "column", gap: 28, alignItems: "center" },
  panel: {
    borderLeft: "1px solid #d3d8d4", background: "#fff", display: "flex",
    flexDirection: "column", height: "100vh", overflow: "hidden",
  },
  panelHead: { padding: "20px 22px 14px", borderBottom: "1px solid #eef1ef" },
  marca: { fontSize: 11, fontWeight: 700, color: TEAL, letterSpacing: ".04em", textTransform: "uppercase" },
  h1: { fontSize: 19, fontWeight: 700, margin: "6px 0 3px", letterSpacing: "-.02em" },
  sub: { fontSize: 13, color: "#8a8f8d", margin: 0, lineHeight: 1.4 },
  panelBody: { flex: 1, overflowY: "auto", padding: "16px 22px" },
  stat: { display: "flex", alignItems: "baseline", gap: 5, marginBottom: 20 },
  statNum: { fontSize: 26, fontWeight: 700, color: TEAL_BRILLANTE, fontVariantNumeric: "tabular-nums" },
  statLbl: { fontSize: 12, color: "#8a8f8d" },
  propGroup: { display: "flex", flexDirection: "column" },
  propTit: { fontSize: 11, textTransform: "uppercase", letterSpacing: ".05em",
    color: "#8a8f8d", fontWeight: 700, marginBottom: 12 },
  lbl: { fontSize: 11, color: "#6b706e", fontWeight: 600, marginBottom: 4, marginTop: 10 },
  input: { width: "100%", padding: "8px 10px", border: "1px solid #d3d8d4", borderRadius: 8,
    fontSize: 13, boxSizing: "border-box", outlineColor: TEAL },
  propRow: { display: "flex", gap: 10 },
  segmento: { display: "flex", gap: 4, background: "#eef2f3", padding: 3, borderRadius: 9 },
  segBtn: { flex: 1, padding: "7px", border: "none", borderRadius: 6, background: "transparent",
    fontSize: 12, fontWeight: 600, color: "#6b706e", cursor: "pointer" },
  segBtnOn: { background: "#fff", color: TEAL, boxShadow: "0 1px 3px rgba(0,0,0,.1)" },
  tipoTag: { marginTop: 16, alignSelf: "flex-start", fontSize: 11, fontWeight: 600,
    color: TEAL_OSC, background: "#DDEAEE", padding: "4px 10px", borderRadius: 6 },
  vacio: { padding: "20px 0", fontSize: 13, lineHeight: 1.5 },
  panelFoot: { padding: "12px 22px", borderTop: "1px solid #eef1ef", background: "#fafbfa" },
  btnGenerar: {
    width: "100%", background: TEAL, color: "#fff", border: "none", borderRadius: 10,
    padding: "12px", fontSize: 14, fontWeight: 700, cursor: "pointer", marginBottom: 8,
  },
  avisoGuardado: {
    marginBottom: 8, padding: "8px 10px", background: "#E1F5EE", border: "1px solid #9FE1CB",
    borderRadius: 7, fontSize: 12, color: "#0F6E56",
  },
  footHint: { fontSize: 11, color: "#a0a5a2", margin: 0 },
};

const HS = {
  hoja: {
    position: "relative", background: "#fff", boxShadow: "0 2px 16px rgba(0,0,0,.12)",
    borderRadius: 2, flexShrink: 0,
  },
  grid: {
    position: "absolute", inset: 0, pointerEvents: "none", opacity: 0.5,
    backgroundImage:
      "linear-gradient(#00000008 1px, transparent 1px), linear-gradient(90deg, #00000008 1px, transparent 1px)",
  },
  fid: { position: "absolute", background: TINTA, borderRadius: 1 },
  numero: { position: "absolute", bottom: 6, right: 10, fontSize: 10, color: "#c4c9c5", fontWeight: 600 },
};

const CS = {
  wrap: { position: "absolute", cursor: "grab", padding: 4, borderRadius: 4, userSelect: "none" },
  pregunta: { fontSize: 9 * ESCALA * 1.3, fontWeight: 700, color: TINTA, marginBottom: 3, whiteSpace: "nowrap" },
  burbuja: { border: `1.4px solid ${TINTA}`, borderRadius: "50%", boxSizing: "border-box", flexShrink: 0 },
  digitoLbl: { position: "absolute", fontSize: 7 * ESCALA * 1.3, color: "#999",
    height: 15 * ESCALA, display: "flex", alignItems: "center" },
  numLikert: { fontSize: 6 * ESCALA * 1.4, color: "#999", marginTop: 1 },
  optLbl: { fontSize: 8 * ESCALA * 1.3, color: TINTA, whiteSpace: "nowrap" },
};